import { isNil, isObject } from 'lodash'

import { IS_DEVELOPMENT } from '@root/app-modes'

/**
 * Resolves the root element used to initialize the application.
 *
 * In development mode, returns the original root element unchanged.
 * In Forge, creates a temporary DOM element and populates its dataset
 *
 * @param {Object} params
 * @param {HTMLElement} params.originalRootElement - The original application root element.
 * @param {Record<string, any>} [params.pluginOptions={}]
 *   Plugin options to be exposed through the element's dataset.
 *
 * @returns {HTMLElement}
 *
 * @throws {TypeError} If `originalRootElement` is not an HTMLElement.
 */
export const resolveRootElement = ({ originalRootElement, pluginOptions = {} } = {}) => {
  if (!(originalRootElement instanceof HTMLElement)) {
    throw new TypeError('originalRootElement must be an HTMLElement')
  }

  if (!isObject(pluginOptions)) {
    throw new TypeError('pluginOptions must be an object')
  }

  if (IS_DEVELOPMENT) return originalRootElement

  // In Forge mode invoke() returns options as a plain object, not via rootElement.dataset.x`
  // But createAuthApp() requires a DOM element. So we create a fake one and populate dataset with the options.
  const fakeRootElement = document.createElement('div')
  for (const k of Object.keys(pluginOptions)) {
    const v = pluginOptions[k]

    if (!isNil(v)) {
      fakeRootElement.dataset[k] = v
    }
  }

  return fakeRootElement
}
